import { useToast } from '@scality/core-ui';
import type { CoreUITheme } from '@scality/core-ui/dist/style/theme';
import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import { logError } from '../../utils/logger';
import { downloadNodeAs, type ExportFormat, exportFilename } from './renderToFile';

const SETTLE_TIMEOUT_MS = 5000;
const STABLE_FRAMES = 3;
const FRAME_FALLBACK_MS = 20;

/** The timer matters: browsers stop firing animation frames in a background tab. */
const nextFrame = (): Promise<void> =>
  new Promise((resolve) => {
    const settle = () => {
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(settle, FRAME_FALLBACK_MS);
    requestAnimationFrame(settle);
  });

/**
 * The board's charts size themselves from a `ResizeObserver`, so they are drawn a frame or two
 * after mount and change the board's height as they grow. Re-read the node each frame: StrictMode
 * can remount the board mid-wait, and capturing the detached one exports an empty sheet.
 */
const waitUntilDrawn = async (nodeOf: () => HTMLElement | null): Promise<void> => {
  const deadline = performance.now() + SETTLE_TIMEOUT_MS;
  let previousHeight = -1;
  let stableFrames = 0;

  while (performance.now() < deadline) {
    await nextFrame();
    const height = nodeOf()?.scrollHeight ?? 0;
    stableFrames = height > 0 && height === previousHeight ? stableFrames + 1 : 0;
    previousHeight = height;
    if (stableFrames >= STABLE_FRAMES) return;
  }
};

type ExportRequest = { id: number; format: ExportFormat };

export type DashboardExportController = {
  boardRef: RefObject<HTMLDivElement | null>;
  /** The board only exists while an export runs. */
  isBoardMounted: boolean;
  exportingFormat: ExportFormat | null;
  startExport: (format: ExportFormat) => void;
};

/**
 * Mounts the off-screen board, lets it draw, captures it, hands over the file, unmounts. Keeping
 * the board mounted between exports would mean a second rendering of every widget at all times.
 */
export const useDashboardExport = (dashboardName: string): DashboardExportController => {
  const theme = useTheme() as CoreUITheme;
  const toast = useToast();
  const boardRef = useRef<HTMLDivElement | null>(null);
  const [request, setRequest] = useState<ExportRequest | null>(null);
  const nextId = useRef(0);
  /** Last request the effect picked up, so StrictMode's second run doesn't export twice. */
  const startedId = useRef(0);

  const startExport = useCallback(
    (format: ExportFormat) => {
      // Swapping the format under a capture in flight would write its image into the new file.
      if (request) return;
      nextId.current += 1;
      setRequest({ id: nextId.current, format });
    },
    [request],
  );

  // Deliberately an effect: there is nothing to capture until React has committed the board.
  useEffect(() => {
    if (!request || startedId.current === request.id) return;
    startedId.current = request.id;

    (async () => {
      try {
        await waitUntilDrawn(() => boardRef.current);
        const board = boardRef.current;
        if (!board) throw new Error('The dashboard export board never mounted');
        await downloadNodeAs(
          request.format,
          board,
          exportFilename(dashboardName, request.format),
          theme.backgroundLevel1,
        );
      } catch (error) {
        logError(error, 'Dashboard export failed');
        toast.showToast({
          status: 'error',
          autoDismiss: true,
          open: true,
          message: `Could not export the dashboard as ${request.format.toUpperCase()}.`,
        });
      } finally {
        setRequest(null);
      }
    })();
  }, [request, dashboardName, theme.backgroundLevel1, toast]);

  return {
    boardRef,
    isBoardMounted: request !== null,
    exportingFormat: request?.format ?? null,
    startExport,
  };
};
